<template>
  <!--
    The class a caller writes lands on the box, not on the <input> inside it.
    It used to go inwards with the rest of $attrs, which meant a `flex: 1 1 200px`
    meant for the field sized the text node within it instead, and the field
    itself came out whatever width its content happened to want.
  -->
  <div
    class="input"
    :class="[$attrs.class, { 'input--invalid': invalid }]"
    :style="$attrs.style"
  >
    <slot name="prefix" />
    <input
      ref="control"
      v-bind="controlAttrs"
      :id="controlId"
      :type="revealed ? 'text' : type"
      :value="shown"
      :aria-invalid="invalid || undefined"
      class="input__el"
      @beforeinput="onBeforeInput"
      @input="onInput"
    />
    <!-- Let people check what they typed: blind password fields cause more
         lockouts than they prevent. -->
    <button
      v-if="type === 'password'"
      type="button"
      class="input__reveal"
      :aria-label="revealed ? uiLabel('hidePassword') : uiLabel('showPassword')"
      tabindex="-1"
      @click="revealed = !revealed"
    >
      {{ revealed ? '○' : '●' }}
    </button>
    <slot name="suffix" />
  </div>
</template>

<script setup>
import { computed, inject, nextTick, ref, useAttrs } from 'vue'

import { FIELD_ID } from '../utils/fieldId'

import { uiLabel } from '../utils/labels'

defineOptions({ inheritAttrs: false })
const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  type: { type: String, default: 'text' },
  id: String,
  invalid: Boolean,
  /**
   * What `v-model.number` and `v-model.trim` become on a component.
   *
   * Vue applies those modifiers itself on a plain <input>, but on a component
   * it only hands them over as this prop and expects the component to do the
   * work. Without it they were silently inert: half the repository writes
   * `v-model.number` on a number field and every one of them was getting a
   * string back, which the servers happened to coerce. Honouring them here is
   * the one place that fixes every caller at once.
   */
  modelModifiers: { type: Object, default: () => ({}) },
  /**
   * A mask: how the value is shown, and how it is kept.
   *
   * `format` turns what is stored into what is displayed (an IBAN in groups of
   * four); `parse` turns what was typed into what is stored (that same IBAN
   * with no spaces, upper case). Either can be given alone. `utils/mask.js`
   * ships the pairs — `mask.iban.show` / `mask.iban.store`.
   *
   * The point of doing it HERE rather than in each form: a field that
   * reformats as you type moves the caret, and the fix for that is fiddly
   * enough that the second screen to try it would get it subtly wrong.
   */
  format: { type: Function, default: null },
  parse: { type: Function, default: null },
})

// El id que pone la etiqueta del campo, cuando el llamante no ha dado uno.
const fieldId = inject(FIELD_ID, null)
const controlId = computed(() => props.id ?? fieldId)
const emit = defineEmits(['update:modelValue'])

const control = ref(null)

/**
 * Put the cursor in the box.
 *
 * Exposed because the caller cannot reach the <input>: this component's root is
 * the frame around it, so every screen that wanted to focus a field was
 * reaching in with a querySelector and would break the day the markup gains a
 * wrapper. One method here is the contract that cannot.
 */
defineExpose({
  focus: (options) => control.value?.focus(options),
  select: () => control.value?.select(),
})

/** What the box shows: the stored value, dressed. */
const shown = computed(() => (props.format ? props.format(props.modelValue ?? '') : props.modelValue))

/**
 * How many characters of the STORED value sit behind this point on screen.
 *
 * The unit the caret is kept in. Positions move when the mask regroups; the
 * count of real characters does not.
 */
function significantBefore(text, index) {
  return props.parse ? props.parse(String(text).slice(0, index)).length : index
}

/** The screen position that holds `wanted` real characters behind it. */
function positionOf(text, wanted) {
  let index = 0
  while (index < text.length && significantBefore(text, index) < wanted) index += 1
  return index
}

/**
 * Backspace and Delete across a separator the mask put there.
 *
 * Without this, deleting at a group boundary appears to do nothing: the
 * browser removes the space, the mask puts it straight back, and the value is
 * unchanged — so every four digits of an IBAN need the key pressed twice, and
 * the second press is the one that works. What people do instead is hold the
 * key down and take out more than they meant.
 *
 * Only the boundary case is intercepted. Deleting a real character is the
 * browser's job and it does it correctly.
 */
function onBeforeInput(event) {
  if (!props.parse) return
  const backwards = event.inputType === 'deleteContentBackward'
  const forwards = event.inputType === 'deleteContentForward'
  if (!backwards && !forwards) return

  const element = event.target
  const start = element.selectionStart
  const end = element.selectionEnd
  // A selection is a normal delete: what is highlighted goes.
  if (start === null || start !== end) return

  const text = element.value
  const behind = significantBefore(text, start)

  // Is the character being deleted one of ours? If the count of real
  // characters does not change when it goes, it is a separator.
  const stillThere = backwards
    ? significantBefore(text, start - 1) === behind
    : significantBefore(text, start + 1) === behind
  if (!stillThere) return

  const stored = props.parse(text)
  const at = backwards ? behind - 1 : behind
  if (at < 0 || at >= stored.length) {
    // Nothing real on that side — swallow it so the caret does not drift into
    // the padding and pretend something happened.
    event.preventDefault()
    return
  }

  event.preventDefault()
  const next = stored.slice(0, at) + stored.slice(at + 1)
  emit('update:modelValue', next)

  nextTick(() => {
    const box = control.value
    if (!box || document.activeElement !== box) return
    if (box.value !== shown.value) box.value = shown.value ?? ''
    try { box.setSelectionRange(positionOf(box.value, at), positionOf(box.value, at)) } catch { /* not a text field */ }
  })
}

function onInput(event) {
  const element = event.target
  let value = element.value

  if (props.modelModifiers.trim) value = value.trim()
  if (props.modelModifiers.number) {
    const parsed = parseFloat(value)
    // An empty box is empty, not zero: coercing it would make a field somebody
    // is in the middle of clearing jump back to 0 under their cursor.
    value = Number.isNaN(parsed) ? value : parsed
  }

  if (!props.parse && !props.format) {
    emit('update:modelValue', value)
    return
  }

  // ── Typing inside a mask ───────────────────────────────────────────────────
  //
  // Reformatting rewrites the whole box, and the browser then drops the caret
  // at the end — so correcting the third group of an IBAN sends the cursor to
  // the far right after every keystroke. What is kept steady is not the caret
  // POSITION but how many real characters sit behind it: the spaces are ours,
  // and nobody is counting them.
  const caret = element.selectionStart ?? value.length
  const significant = significantBefore(value, caret)

  const stored = props.parse ? props.parse(value) : value
  emit('update:modelValue', stored)

  nextTick(() => {
    const box = control.value
    if (!box || document.activeElement !== box) return

    // Typing a character the mask throws away — a space inside an IBAN — leaves
    // the stored value unchanged, so Vue has nothing to re-render and the box
    // keeps the stray character. Putting the dressed value back by hand is the
    // only way the field and the model stay the same thing.
    if (box.value !== shown.value) box.value = shown.value ?? ''

    // A number box has no selection to set, and asking throws in some browsers.
    const index = positionOf(box.value, significant)
    try { box.setSelectionRange(index, index) } catch { /* not a text field */ }
  })
}

const attrs = useAttrs()
const controlAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs
  return rest
})

const revealed = ref(false)
</script>

<style scoped>
.input {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 0 var(--space-3);
  height: 38px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  transition: border-color var(--fast), box-shadow var(--fast);
}
.input:focus-within {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-ring);
}
.input--invalid { border-color: var(--color-danger); }
.input--invalid:focus-within { box-shadow: 0 0 0 3px var(--color-danger-soft); }

.input__el {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: var(--text-field);
  /* Que ocupe TODO el alto de la caja. Medía 25 px dentro de una de 38: los
     seis píxeles de arriba y los seis de abajo parecían el campo y no lo eran,
     porque tocar el relleno de un contenedor no enfoca al hijo. En un ERP
     entero de formularios eso es un toque fallado cada pocos campos. */
  align-self: stretch;
}
.input__el::placeholder { color: var(--color-text-mute); }
.input__el:disabled { color: var(--color-text-mute); cursor: not-allowed; }

.input__reveal {
  border: none;
  background: none;
  color: var(--color-text-mute);
  font-size: 10px;
  padding: var(--space-1);
  line-height: 1;
}

/* ── Con el dedo ────────────────────────────────────────────────────────────
   La caja sube a 44 px y el ojo de la contraseña con ella: medía 23x28 y está
   pegado al borde derecho de la pantalla, que es el peor sitio para fallar.
   `pointer: coarse` y no un ancho de pantalla: lo que decide es con qué se
   toca — una tableta con teclado tiene pantalla de sobra y se sigue tocando
   con el dedo. */
@media (pointer: coarse) {
  .input { height: 44px; }
  .input__reveal {
    min-width: 44px;
    align-self: stretch;
    font-size: var(--text-xs);
    /* El relleno lateral se come el hueco de la caja, así que se recorta para
       que el campo no pierda ancho de escritura. */
    padding-inline: 0;
  }
}
.input__reveal:hover { color: var(--color-text); }

/* Taller under a thumb, matching UiSelect so a form stays on one rhythm. */
@media (pointer: coarse) {
  .input { height: 44px; }
  .input__reveal { padding: var(--space-2); font-size: 12px; }
}
</style>
